import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PublicNav from "@/components/PublicNav";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setErr("This link is missing its token."); return; }
    api.get(`/invites/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invite not found"));
  }, [token]);

  const accept = async () => {
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      try { await refresh(); } catch {}
      toast.success("Welcome to the workspace");
      localStorage.setItem("fx_ws", data.workspace_id);
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept invite");
    }
  };

  return (
    <div className="min-h-screen">
      <PublicNav />
      <div className="max-w-md mx-auto p-8" data-testid="accept-page">
        <div className="fx-panel p-8">
          <h1 className="font-display text-2xl font-bold text-white">Join a Fexidoc workspace</h1>
          {err && <div className="mt-4 text-sm text-amber-300">{err}</div>}
          {invite && (
            <div className="mt-4 space-y-4">
              <div className="text-sm text-slate-300">
                You are invited to <span className="text-white font-medium">{invite.workspace?.name || "a workspace"}</span> as a <span className="text-cyan-300">{invite.role}</span>.
              </div>
              {!user ? (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">Sign in or create an account with <span className="text-slate-300">{invite.email}</span> to accept.</div>
                  <GoogleSignInButton next={`/accept-invite?token=${token}`} testid="accept-google" />
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-[#1E293B]" />
                    <span className="text-[11px] uppercase tracking-widest text-slate-500">or use email</span>
                    <div className="flex-1 h-px bg-[#1E293B]" />
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/login?next=/accept-invite?token=${token}`} className="fx-btn-primary text-white font-medium px-4 py-2 rounded-lg text-sm" data-testid="accept-signin">Sign in</Link>
                    <Link to={`/signup?email=${encodeURIComponent(invite.email)}&next=/accept-invite?token=${token}`} className="border border-white/10 hover:border-white/20 text-slate-200 px-4 py-2 rounded-lg text-sm" data-testid="accept-signup">Create account</Link>
                  </div>
                </div>
              ) : user.email.toLowerCase() !== invite.email.toLowerCase() ? (
                <div className="text-xs text-amber-300">This invite was sent to <span className="text-slate-200">{invite.email}</span>, but you are signed in as {user.email}. Sign out and use the invited email.</div>
              ) : (
                <button onClick={accept} className="fx-btn-primary text-white font-medium px-4 py-2 rounded-lg text-sm" data-testid="accept-btn">Accept invitation</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
